<script setup lang="ts">
import type StrengthBar from '@/components/auth/StrengthBar.vue';
import { useBiometricAuth } from '~/composables/useBiometricAuth';

type Props = {
  opacity?: boolean;
  onlyForm?: boolean;
  buttonText?: string;
};

const {
  opacity = false,
  onlyForm = false,
  buttonText = '',
} = defineProps<Props>();

const emit = defineEmits(['check-pendency']);

const route = useRoute();
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { changePassword } = useAuthStore();
const { userId, userEmail, userHasStrongPassword, fetchUserProfile } =
  useUserData();

const {
  isAvailableEnvironment: isNativeAppEnv,
  checkAvailability,
  enroll,
  disable,
  markCredentialOwner,
  clearCredentialOwner,
} = useBiometricAuth();

const { validationData } = storeToRefs(useRegisterStore());
const { $reset } = useRegisterStore();

const isLoading = ref(false);
const isEditing = ref<boolean>(!!onlyForm);

const strengthBarRef = ref<InstanceType<typeof StrengthBar> | null>(null);

const formFields = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const isPasswordValid = computed(() => {
  return (
    validationData.value.fields.password &&
    formFields.value.newPassword === formFields.value.confirmPassword
  );
});

async function onEditPasswordSubmit() {
  if (!userId.value || !isPasswordValid.value) {
    return;
  }

  if (!formFields.value.currentPassword.length) {
    openToaster({
      message: t('auth.change_password.error.current_password'),
      type: 'error',
    });

    return;
  }

  if (!formFields.value.newPassword) {
    openToaster({
      message: t('auth.change_password.error.new_password'),
      type: 'error',
    });

    return;
  }

  if (formFields.value.newPassword !== formFields.value.confirmPassword) {
    openToaster({
      message: t('auth.change_password.error.password_confirmation'),
      type: 'error',
    });

    return;
  }

  if (
    formFields.value.currentPassword.length < 6 ||
    formFields.value.newPassword.length < 6 ||
    formFields.value.confirmPassword.length < 6
  ) {
    openToaster({
      message: t('auth.change_password.error.min_length'),
      type: 'error',
    });

    return;
  }

  await $containment.handle('update_data', () => {
    sendForm();
  });
}

async function updateBiometricCredential(newPassword: string): Promise<void> {
  if (!isNativeAppEnv()) return;

  const availability = await checkAvailability();

  if (!availability.hasCredential) return;

  const result = await enroll(userEmail.value, newPassword);

  if (result.success) {
    markCredentialOwner(String(userId.value));

    openToaster({
      message: 'Biometria atualizada com a nova senha.',
      type: 'success',
    });

    return;
  }

  // Cancelou ou erro: credencial antiga é inválida, deve ser apagada.
  await disable();
  clearCredentialOwner();

  openToaster({
    message:
      'Biometria desativada pois a senha foi alterada. Reative em Login e Segurança.',
    type: 'warning',
  });
}

async function sendForm() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  const sendData: IChangePassword = {
    current_password: formFields.value.currentPassword,
    new_password: formFields.value.newPassword,
    new_password_confirmation: formFields.value.confirmPassword,
  };

  if ($kyc?.current?.id) {
    sendData.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    sendData.password_check = temporaryPassword.get();
  }

  isLoading.value = true;

  await changePassword(userId.value, sendData)
    .then(async (data) => {
      if (data.status.toString().startsWith('20')) {
        const message =
          data.message ??
          data.data.message ??
          t('auth.change_password.form_success');

        openToaster({
          message,
          type: 'success',
        });

        await fetchUserProfile({ force: true });

        isEditing.value = false;

        await updateBiometricCredential(formFields.value.newPassword);
      } else {
        isLoading.value = false;

        const message = data.message
          ? data.message
          : data.errors[0]
            ? data.errors[0]
            : t('auth.login.error.reset_password');

        openToaster({
          message,
          type: 'error',
        });
      }
    })
    .catch((err) => {
      const errorMessage =
        err?.data && err.data?.message
          ? err.data.message
          : t('auth.change_password.error.current_password_invalid');

      openToaster({
        message: errorMessage,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
      emit('check-pendency');
      temporaryPassword.clear();
    });
}

function updatePasswordStrength() {
  strengthBarRef.value?.updatePasswordStrength();
}

const toggleEdit = () => {
  isEditing.value = !isEditing.value;
};

const hasPendency = computed(() => {
  return !userHasStrongPassword.value;
});

defineExpose({
  name: 'password',
  isEditing,
  hasPendency,
  toggleEdit,
});

onMounted(() => {
  if (route.query?.highlight) {
    isEditing.value = true;
  }
});

onUnmounted(() => {
  $reset();
});
</script>

<template>
  <client-only>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />
    <div :class="[$style.toggleBox, { [$style.opacity]: opacity }]">
      <div
        v-if="!onlyForm"
        :class="$style.header"
      >
        <div :class="$style.title">
          {{ $t('account.security.change_password') }}
        </div>
        <div
          v-if="!userHasStrongPassword"
          :class="[$style.pill, $style.redPill]"
        >
          <NuxtIcon name="close" />
          {{ $t('account.security.weak_password') }}
        </div>
        <div
          v-else
          :class="[$style.pill, $style.greenPill]"
        >
          <NuxtIcon name="check" />
          {{ $t('account.security.strong_password') }}
        </div>
        <button
          :class="$style.edit"
          @click="toggleEdit"
        >
          {{ isEditing ? $t('general.cancel') : $t('general.edit') }}
        </button>
      </div>

      <div
        v-if="!isEditing"
        :class="$style.viewContent"
      >
        <p :class="$style.text"> ********** </p>
      </div>

      <form
        v-else
        :class="$style.formContainer"
        @submit.prevent="onEditPasswordSubmit"
      >
        <div
          v-if="!onlyForm"
          :class="$style.description"
        >
          {{ $t('account.security.strong_password_description') }}
        </div>

        <div :class="$style.formGroup">
          <label for="currentPassword">
            {{ $t('account.security.current_password') }}<span>*</span>
          </label>

          <BaseFormInput
            id="currentPassword"
            v-model="formFields.currentPassword"
            autocomplete="current-password"
            name="currentPassword"
            :placeholder="$t('account.security.current_password')"
            :required="true"
            :show-hide-pass="true"
          />
        </div>

        <div :class="$style.formGroup">
          <label for="newPassword">
            {{ $t('account.security.new_password') }}<span>*</span>
          </label>

          <BaseFormInput
            id="newPassword"
            v-model="formFields.newPassword"
            autocomplete="new-password"
            name="newPassword"
            :placeholder="$t('account.security.new_password')"
            :required="true"
            :show-hide-pass="true"
            @keyup="updatePasswordStrength"
          />
        </div>

        <div :class="$style.formGroup">
          <label for="newPassword">
            {{ $t('account.security.confirm_password') }}<span>*</span>
          </label>

          <BaseFormInput
            id="confirmPassword"
            v-model="formFields.confirmPassword"
            autocomplete="new-password"
            name="confirmPassword"
            :placeholder="$t('account.security.confirm_password')"
            :required="true"
            :show-hide-pass="true"
          />
        </div>

        <div class="pt-3 pb-1 flex flex-col gap-5">
          <AuthStrengthBar
            ref="strengthBarRef"
            :password="formFields.newPassword"
          />
        </div>

        <div class="flex justify-between items-end w-full col-span-2 mt-4">
          <button
            :class="$style.btnSubmit"
            :disabled="!isPasswordValid"
            type="submit"
          >
            <NuxtIcon name="check" />
            {{ buttonText || $t('auth.change_password.send_btn') }}
          </button>
        </div>
      </form>
    </div>
  </client-only>
</template>

<style module lang="scss">
.toggleBox {
  @apply flex flex-col gap-3 p-1 md:p-0 px-1 md:px-2 w-full;

  &.opacity {
    @apply opacity-20 pointer-events-none select-none;
  }

  .header {
    @apply flex items-center justify-between;

    .title {
      @apply text-sm font-semibold;
    }

    .pill {
      @apply bg-texts/20 text-white px-1.5 py-0.5 rounded-full text-[.6rem] font-semibold leading-3 mr-auto ml-2 flex items-center gap-1;

      &.redPill {
        @apply bg-error;
      }

      &.greenPill {
        @apply bg-success;
      }
    }

    .edit {
      @apply text-xs font-bold uppercase opacity-60 px-1;
    }
  }

  .viewContent {
    @apply text-base text-texts/80;

    .text {
      @apply text-xs;
    }
  }

  .formContainer {
    .description {
      @apply text-sm md:text-sm text-texts/70 mb-4 max-w-[500px];
    }

    .formGroup {
      @apply mb-2;

      label {
        @apply text-xs font-medium;
        span {
          @apply text-error;
        }
      }
    }

    .btnSubmit {
      @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed;
    }
  }
}
</style>
